import Service, { service } from '@ember/service';
import {
  QUICK_FEEDBACK_MESSAGES_PAGE_SIZE,
  QUICK_FEEDBACK_SENTIMENTS,
} from 'admin-panel/constants/dashboard';
import {
  bucketDashboardPoints,
  type DashboardAggregation,
  type DashboardBucket,
  type DashboardPoint,
} from 'admin-panel/utils/dashboard-buckets';

import type StoreService from '@ember-data/store';
import type CategoryModel from 'admin-panel/models/category';
import type HomeScreenModel from 'admin-panel/models/home-screen';
import type HotelModel from 'admin-panel/models/hotel';
import type DeepDiveService from 'admin-panel/services/deep-dive';
import type ItemModel from 'admin-panel/models/item';
import type PageModel from 'admin-panel/models/page';
import type KeenService from 'admin-panel/services/keen';
import type {
  KeenAnalysis,
  KeenFilter,
  KeenGroupedRow,
  KeenIntervalRow,
} from 'admin-panel/services/keen';
import type {
  CommerceDashboardMetric,
  DashboardChartMode,
  DashboardHeatmapPayload,
  QuickFeedbackSentiment,
  UsageDashboardMetric,
} from 'admin-panel/types/dashboard';
import type { DashboardTimeframe } from 'admin-panel/utils/dashboard-timeframe';

export interface DashboardCardAvailability {
  greenOption: boolean;
  quickFeedback: boolean;
}

export interface QuickFeedbackMessage {
  id: string;
  createdAt: string;
  satisfaction: QuickFeedbackSentiment;
  message?: string;
  roomCode?: string;
}

export interface DashboardListRow {
  id: string;
  value: number;
  title?: string;
  categoryId?: string;
  average?: number;
}

interface ChartQuery {
  collection: string;
  filters: KeenFilter[];
  value: { analysis: KeenAnalysis; targetProperty?: string };
  countFilters?: KeenFilter[];
  aggregation: DashboardAggregation;
}

interface LoadOptions {
  categoryId?: string;
  signal?: AbortSignal;
}

interface CardAvailabilityRow {
  has_green_option?: boolean | null;
  has_quick_feedback?: boolean | null;
}

interface GreenOptionRequestsRow {
  hour_start: string;
  requests?: number | string | null;
}

interface GreenOptionSkippedCleaningsRow {
  skipped_cleanings?: number | string | null;
}

interface QuickFeedbackHourRow {
  hour_start: string;
  satisfaction: string;
  value?: number | string | null;
}

interface QuickFeedbackMessageRow {
  id: number | string;
  created_at: string;
  satisfaction: string;
  message?: string | null;
  room_code?: string | null;
  total?: number | string | null;
}

interface KeenOrderItem {
  item_id?: number | string | null;
  total_value?: number | null;
}

const MAX_SESSION_DURATION_SECONDS = 3600;
const CATEGORY_PAGE_SIZE = 100;

export default class DashboardAnalyticsService extends Service {
  @service declare deepDive: DeepDiveService;
  @service declare keen: KeenService;
  @service declare store: StoreService;

  async loadUsageTotals(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<Record<UsageDashboardMetric, number>> {
    const filters = usageFilters(hotel);
    const [pageViews, sessions, avgSessionDuration] = await Promise.all([
      this.value('count', 'page_views', filters, timeframe, signal),
      this.value('count', 'sessions', filters, timeframe, signal),
      this.value(
        'average',
        'sessions',
        [...filters, sessionDurationFilter()],
        timeframe,
        signal,
        'duration'
      ),
    ]);

    return { pageViews, sessions, avgSessionDuration };
  }

  loadUsageChart(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: UsageDashboardMetric,
    mode: DashboardChartMode,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const filters = withCategory(usageFilters(hotel), categoryId);
    const sessionFilters = [...filters, sessionDurationFilter()];
    const query: ChartQuery =
      metric === 'avgSessionDuration'
        ? {
            collection: 'sessions',
            filters: sessionFilters,
            value: { analysis: 'sum', targetProperty: 'duration' },
            countFilters: sessionFilters,
            aggregation: 'ratio',
          }
        : {
            collection: metric === 'pageViews' ? 'page_views' : 'sessions',
            filters,
            value: { analysis: 'count' },
            aggregation: 'sum',
          };

    return this.chart(query, timeframe, mode, signal);
  }

  async loadPageViewsByFolder(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ) {
    return this.grouped(
      'count',
      'page_views',
      usageFilters(hotel),
      'category_id',
      timeframe,
      signal
    );
  }

  async loadPageViewsByPage(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const rows = await this.grouped(
      'count',
      'page_views',
      withCategory(usageFilters(hotel), categoryId),
      'page_id',
      timeframe,
      signal
    );

    return this.withTitles(rows, 'page');
  }

  async loadCommerceTotals(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<Record<CommerceDashboardMetric, number>> {
    const filters = commerceFilters(hotel);
    const [revenue, orders] = await Promise.all([
      this.value('sum', 'orders', filters, timeframe, signal, 'total'),
      this.value('count', 'orders', filters, timeframe, signal),
    ]);

    return { revenue, orders };
  }

  loadCommerceChart(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    mode: DashboardChartMode,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const filters = withCategory(commerceFilters(hotel), categoryId);
    const showsAverageOrderValue = metric === 'revenue' && mode !== 'totals';

    return this.chart(
      {
        collection: 'orders',
        filters,
        value:
          metric === 'revenue'
            ? { analysis: 'sum', targetProperty: 'total' }
            : { analysis: 'count' },
        countFilters: showsAverageOrderValue
          ? [...filters, pricedOrderFilter()]
          : undefined,
        aggregation: showsAverageOrderValue ? 'ratio' : 'sum',
      },
      timeframe,
      mode,
      signal
    );
  }

  async loadCommerceByFolder(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    { signal }: LoadOptions = {}
  ): Promise<DashboardListRow[]> {
    const filters = commerceFilters(hotel);

    if (metric === 'orders') {
      return this.grouped(
        'count',
        'orders',
        filters,
        'category_id',
        timeframe,
        signal
      );
    }

    const [revenueRows, pricedOrderRows] = await Promise.all([
      this.grouped(
        'sum',
        'orders',
        filters,
        'category_id',
        timeframe,
        signal,
        'total'
      ),
      this.grouped(
        'count',
        'orders',
        [...filters, pricedOrderFilter()],
        'category_id',
        timeframe,
        signal
      ),
    ]);
    const pricedOrders = new Map(
      pricedOrderRows.map(({ id, value }) => [id, value])
    );

    return revenueRows.map((row) => {
      const orders = pricedOrders.get(row.id);

      return orders ? { ...row, average: row.value / orders } : row;
    });
  }

  async loadCommerceByItem(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const orders = await this.keen.query<{ order_items?: KeenOrderItem[] }[]>(
      'extraction',
      {
        event_collection: 'orders',
        filters: withCategory(commerceFilters(hotel), categoryId),
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        property_names: ['order_items'],
      },
      { signal }
    );
    const totals = new Map<string, number>();

    orders
      .flatMap((order) => order.order_items || [])
      .forEach((orderItem) => {
        if (orderItem.item_id === null || orderItem.item_id === undefined) {
          return;
        }

        const id = String(orderItem.item_id);
        const value =
          metric === 'revenue' ? Number(orderItem.total_value) || 0 : 1;

        totals.set(id, (totals.get(id) || 0) + value);
      });

    const rows = [...totals]
      .filter(([, value]) => value > 0)
      .map(([id, value]) => ({ id, value }))
      .sort((left, right) => right.value - left.value);

    return this.withTitles(rows, 'item');
  }

  async loadCardAvailability(
    hotel: HotelModel,
    { signal }: LoadOptions = {}
  ): Promise<DashboardCardAvailability> {
    const [row] = await this.deepDive.query<CardAvailabilityRow>(
      'dashboard_card_availability',
      hotel.uuid,
      {},
      { signal }
    );

    return {
      greenOption: row?.has_green_option === true,
      quickFeedback: row?.has_quick_feedback === true,
    };
  }

  async loadGreenOptionRequests(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<DashboardPoint[]> {
    const rows = await this.deepDive.query<GreenOptionRequestsRow>(
      'dashboard_green_option_orders_hourly',
      hotel.uuid,
      deepDiveRange(timeframe),
      { signal }
    );

    return rows.map((row) => ({
      at: new Date(row.hour_start).toISOString(),
      value: Number(row.requests) || 0,
    }));
  }

  async loadGreenOptionSkippedCleanings(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<number> {
    const [row] = await this.deepDive.query<GreenOptionSkippedCleaningsRow>(
      'dashboard_green_option_skipped_cleanings',
      hotel.uuid,
      deepDiveRange(timeframe),
      { signal }
    );

    return Number(row?.skipped_cleanings) || 0;
  }

  async loadQuickFeedback(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<Record<QuickFeedbackSentiment, DashboardPoint[]>> {
    const rows = await this.deepDive.query<QuickFeedbackHourRow>(
      'dashboard_quick_feedback_hourly',
      hotel.uuid,
      deepDiveRange(timeframe),
      { signal }
    );
    const points: Record<QuickFeedbackSentiment, DashboardPoint[]> = {
      happy: [],
      neutral: [],
      unhappy: [],
    };

    rows.forEach((row) => {
      if (isQuickFeedbackSentiment(row.satisfaction)) {
        points[row.satisfaction].push({
          at: new Date(row.hour_start).toISOString(),
          value: Number(row.value) || 0,
        });
      }
    });

    return points;
  }

  async loadQuickFeedbackMessages(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { offset = 0, signal }: { offset?: number; signal?: AbortSignal } = {}
  ): Promise<{ messages: QuickFeedbackMessage[]; total: number }> {
    const rows = await this.deepDive.query<QuickFeedbackMessageRow>(
      'dashboard_quick_feedback_messages',
      hotel.uuid,
      {
        ...deepDiveRange(timeframe),
        limit: String(QUICK_FEEDBACK_MESSAGES_PAGE_SIZE),
        offset: String(offset),
      },
      { signal }
    );

    return {
      messages: rows.flatMap((row) =>
        isQuickFeedbackSentiment(row.satisfaction)
          ? [
              {
                id: String(row.id),
                createdAt: new Date(row.created_at).toISOString(),
                satisfaction: row.satisfaction,
                message: row.message || undefined,
                roomCode: row.room_code || undefined,
              },
            ]
          : []
      ),
      total: rows.length ? Number(rows[0]?.total) || 0 : offset,
    };
  }

  async loadHomeScreens(hotel: HotelModel) {
    const homeScreens = await this.store.query('home-screen', {
      hotel_id: hotel.id,
    });

    return (homeScreens.toArray() as HomeScreenModel[])
      .filter((homeScreen) => homeScreen.active !== false)
      .sort(
        (left, right) =>
          (left.name || '').localeCompare(right.name || '') ||
          (left.id || '').localeCompare(right.id || '')
      );
  }

  async loadUsageHeatmap(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    homeScreenId: string,
    { signal }: LoadOptions = {}
  ): Promise<DashboardHeatmapPayload> {
    const rows = await this.keen.query<KeenGroupedRow[]>(
      'count',
      {
        event_collection: 'homescreen_clicks',
        filters: [
          ...usageFilters(hotel),
          {
            property_name: 'home_screen_id',
            operator: 'eq',
            property_value: homeScreenId,
          },
        ],
        group_by: 'click.coord',
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
      },
      { signal }
    );
    const points = rows.flatMap((row) => {
      const match = String(row['click.coord'] ?? '').match(/^(\d+)x(\d+)$/);
      const value = Number(row['result']) || 0;

      return match && value > 0
        ? [{ x: Number(match[1]), y: Number(match[2]), value }]
        : [];
    });

    return {
      points,
      maxValue: points.reduce((max, { value }) => Math.max(max, value), 0),
    };
  }

  async loadCategories(hotel: HotelModel) {
    const hotelIds = [hotel.id, ...hotel.hasMany('hotelMixin').ids()];
    const categories: CategoryModel[] = [];

    for (let offset = 0; ; offset += CATEGORY_PAGE_SIZE) {
      const result = await this.store.query('category', {
        'filter[hotel_id_in]': hotelIds,
        limit: CATEGORY_PAGE_SIZE,
        offset,
      });
      const page = result.toArray() as CategoryModel[];
      const total = Number(
        (result as typeof result & { meta?: { total?: number } }).meta?.total
      );

      categories.push(...page);

      if (
        page.length < CATEGORY_PAGE_SIZE ||
        categories.length >= (Number.isFinite(total) ? total : Infinity)
      ) {
        return categories;
      }
    }
  }

  private async value(
    analysis: KeenAnalysis,
    collection: string,
    filters: KeenFilter[],
    timeframe: DashboardTimeframe,
    signal?: AbortSignal,
    targetProperty?: string
  ) {
    const result = await this.keen.query<number | null>(
      analysis,
      {
        event_collection: collection,
        filters,
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        target_property: targetProperty,
      },
      { signal }
    );

    return result ?? 0;
  }

  private async grouped(
    analysis: KeenAnalysis,
    collection: string,
    filters: KeenFilter[],
    groupBy: string,
    timeframe: DashboardTimeframe,
    signal?: AbortSignal,
    targetProperty?: string
  ): Promise<DashboardListRow[]> {
    const rows = await this.keen.query<KeenGroupedRow[]>(
      analysis,
      {
        event_collection: collection,
        filters,
        group_by: groupBy,
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        target_property: targetProperty,
      },
      { signal }
    );

    return rows
      .filter((row) => row[groupBy] !== null && row[groupBy] !== undefined)
      .map((row) => ({
        id: String(row[groupBy]),
        value: Number(row['result']) || 0,
      }))
      .filter((row) => row.value > 0)
      .sort((left, right) => right.value - left.value);
  }

  private async chart(
    query: ChartQuery,
    timeframe: DashboardTimeframe,
    mode: DashboardChartMode,
    signal?: AbortSignal
  ): Promise<DashboardBucket[]> {
    const base = {
      event_collection: query.collection,
      filters: query.filters,
      timeframe: timeframe.keenTimeframe,
      timezone: timeframe.timezone,
      interval:
        mode === 'hourly-average'
          ? ('hourly' as const)
          : timeframe.keenInterval,
    };
    const [valueRows, countRows] = await Promise.all([
      this.keen.query<KeenIntervalRow[]>(
        query.value.analysis,
        { ...base, target_property: query.value.targetProperty },
        { signal }
      ),
      query.countFilters
        ? this.keen.query<KeenIntervalRow[]>(
            'count',
            { ...base, filters: query.countFilters },
            { signal }
          )
        : Promise.resolve([]),
    ]);
    const counts = new Map(
      countRows.map((row) => [row.timeframe.start, row.value ?? 0])
    );
    const points = valueRows.map((row) => ({
      at: row.timeframe.start,
      value: row.value ?? 0,
      count: counts.get(row.timeframe.start) ?? 0,
    }));

    return bucketDashboardPoints(points, timeframe, {
      mode,
      aggregation: query.aggregation,
    });
  }

  private async withTitles(
    rows: DashboardListRow[],
    modelName: 'page' | 'item'
  ) {
    const records = await Promise.allSettled(
      rows.map((row) => this.store.findRecord(modelName, row.id))
    );

    return rows.map((row, index) => {
      const record = records[index];

      if (record?.status !== 'fulfilled') {
        return row;
      }

      const model = record.value as PageModel | ItemModel;
      const categoryId = model.belongsTo('category').id();

      return categoryId
        ? { ...row, title: model.title, categoryId }
        : { ...row, title: model.title };
    });
  }
}

function usageFilters(hotel: HotelModel): KeenFilter[] {
  return [
    { property_name: 'hotel_uuid', operator: 'eq', property_value: hotel.uuid },
  ];
}

function commerceFilters(hotel: HotelModel): KeenFilter[] {
  return [
    {
      property_name: 'hotel_id',
      operator: 'eq',
      property_value: Number(hotel.id),
    },
    { property_name: 'test_order', operator: 'eq', property_value: false },
  ];
}

function pricedOrderFilter(): KeenFilter {
  return { property_name: 'total', operator: 'gte', property_value: 0 };
}

function sessionDurationFilter(): KeenFilter {
  return {
    property_name: 'duration',
    operator: 'lte',
    property_value: MAX_SESSION_DURATION_SECONDS,
  };
}

function isQuickFeedbackSentiment(
  value: string
): value is QuickFeedbackSentiment {
  return (QUICK_FEEDBACK_SENTIMENTS as readonly string[]).includes(value);
}

function deepDiveRange(timeframe: DashboardTimeframe) {
  return {
    timezone: timeframe.timezone,
    starts_at: timeframe.startsAt,
    ends_at: timeframe.endsAt,
  };
}

function withCategory(filters: KeenFilter[], categoryId?: string) {
  return categoryId
    ? [
        ...filters,
        {
          property_name: 'category_id',
          operator: 'eq' as const,
          property_value: Number(categoryId),
        },
      ]
    : filters;
}

declare module '@ember/service' {
  interface Registry {
    'dashboard-analytics': DashboardAnalyticsService;
  }
}
